/**
 * Where the water, the waste, the gas and the power have to land.
 *
 * The one drawing that is not for the customer. A kitchen is installed by three or four
 * trades who each arrive before the cabinets do and each need to know one thing: put it
 * HERE. That information is already in the design -- the sink is at this position on this
 * wall, so the waste is under it -- and nothing was doing the arithmetic.
 *
 * Heights are above finished floor, sourced and named the way guidelines.RULE is, because a
 * number in a drawing with no reason attached is a number somebody will change.
 *
 * Pure. A plan in, a list of points out.
 */
import * as room from './room.js'
import { offsets } from './layout.js'

/**
 * What each service is and where it goes.
 *
 * The heights are Australian domestic practice: a sink waste at 400 clears the trap and the
 * cabinet floor; a general power outlet above a bench at 1150 is above the splashback and
 * below the overheads; a dishwasher and a fridge each want their own outlet, and the fridge's
 * goes to one side so the machine can be pushed back.
 */
export const SERVICE = {
  water: { mark: 'W', name: 'Water', height: 500, note: 'hot and cold, isolating valves in the cabinet' },
  waste: { mark: 'S', name: 'Waste', height: 400, note: '40mm, trap inside the cabinet' },
  gas: { mark: 'G', name: 'Gas', height: 500, note: 'bayonet or hard connection, isolator in reach' },
  power: { mark: 'P', name: 'Power', height: 1150, note: 'double GPO above the benchtop' },
  circuit: { mark: 'C', name: 'Dedicated circuit', height: 500, note: 'own circuit, not shared' },
  duct: { mark: 'D', name: 'Duct', height: 2100, note: 'rangehood to outside, shortest run' },
}

/** Which services each role needs. */
const NEEDS = {
  sink: ['water', 'waste', 'power'],
  dishwasher: ['water', 'waste', 'circuit'],
  cooktop: ['gas', 'circuit'],
  oven: ['circuit'],
  fridge: ['circuit'],
  rangehood: ['power', 'duct'],
}

/**
 * Every service point, on the floor, in room coordinates.
 *
 * @returns {Array<{kind, mark, name, height, note, at: {x,y}, role, itemId, wallId}>}
 */
export function services(plan, maker) {
  const lim = maker.limits
  const out = []
  for (const run of plan.runs) {
    const seg = room.segment(plan, run.wallId)
    if (!seg) continue
    const n = room.inward(seg)
    const at = offsets(run.items)
    run.items.forEach((item, i) => {
      if (item.gap) return
      const spec = maker.cabinets.find((c) => c.id === item.specId)
      const kinds = NEEDS[spec?.role]
      if (!kinds) return
      const centre = run.from + at[i] + item.w / 2
      const depth = run.kind === 'wall' ? lim.wallDepth : lim.baseDepth
      for (const kind of kinds) {
        const s = SERVICE[kind]
        // Behind the cabinet, a third of its depth in: where a plumber drills, and where an
        // electrician can still reach once the carcass is in.
        const p = room.at(seg, centre)
        out.push({
          kind, mark: s.mark, name: s.name, height: s.height, note: s.note,
          at: { x: p.x + n.x * depth * 0.35, y: p.y + n.y * depth * 0.35 },
          role: spec.role, itemId: item.id, wallId: run.wallId,
        })
      }
    })
  }
  return out
}

/**
 * The same list as words, for the maker's paperwork.
 *
 * `along` is the distance from the start of the wall, which is how a tradesperson marks a
 * wall out: they cannot measure from the middle of a cabinet that is not built yet.
 */
export function serviceNotes(plan, maker) {
  const lim = maker.limits
  const out = []
  for (const run of plan.runs) {
    const seg = room.segment(plan, run.wallId)
    if (!seg) continue
    const at = offsets(run.items)
    run.items.forEach((item, i) => {
      if (item.gap) return
      const spec = maker.cabinets.find((c) => c.id === item.specId)
      const kinds = NEEDS[spec?.role]
      if (!kinds) return
      const centre = Math.round(run.from + at[i] + item.w / 2)
      for (const kind of kinds) {
        const s = SERVICE[kind]
        out.push(`${s.mark}  ${s.name} for the ${spec.role} — ${centre}mm along the `
          + `${wallWord(run.wallId, room.isIsland(plan, run.wallId))}, ${s.height}mm above the floor. ${s.note}.`)
      }
    })
  }
  void lim
  return out
}

const wallWord = (id, isle) => isle ? 'island'
  : ({ back: 'back wall', left: 'left wall', right: 'right wall', front: 'facing wall' }[id] ?? `wall ${id}`)
